import * as React from "react";
import {useState} from "react";
import {Document, Sms} from "@falcon/services";
import DocumentBlock from "./DocumentBlock";
import SmsBlock from "./SmsBlock";
import {CommunicationType} from "./CommunicationType";
import {CommunicationCollection} from "./CommunicationCollection";

interface DocumentsProps {
    communicationCollection: CommunicationCollection;
    header: string;
    cssStyles: string;
    navigate: any;
}

function CommunicationList({communicationCollection, header, cssStyles, navigate,}: Readonly<DocumentsProps>): React.JSX.Element {
    const [direction, setDirection] = useState("desc");
    const [active, setActive] = useState(false);
    const maxRecordsCount: number = 5;
    const moreThanFiveRecords: boolean = communicationCollection.items.length > maxRecordsCount;

    function sortByDate(): void {
        setDirection(prevState => (prevState === "asc" ? "desc" : "asc"));
        setActive(true);
    }

    function getSortDirection() {
        return direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
    }

    function renderSortByDate(): React.JSX.Element {
        return (
            <tr>
                <th className={"pb-3 pl-3"}>
                    <span className="documents-archive-table-header muli-semi-bold-16">
                        Date
                    </span>
                    <button
                        type="button"
                        onClick={sortByDate}
                        className={`table-sort-arrow ${active ? getSortDirection() : "fas fa-sort"}`}
                        aria-label="Sort by date"
                    />
                </th>
            </tr>
        );
    }

    function renderDocumentsByDate(documentsCollection: Document[]): React.JSX.Element[] {
        const documents: Document[] = [...documentsCollection].sort((a, b) => {
            const diff = new Date(a.date).getTime() - new Date(b.date).getTime();

            return direction === "asc" ? diff : -diff;
        })
            .slice(0, maxRecordsCount);

        return documents.map((document: Document) => <DocumentBlock key={document.id} document={document}/>);
    }

    function renderSmsByDate(smsCollection: Sms[]): React.JSX.Element[] {
        const smsList: Sms[] = [...smsCollection].sort((a, b) => {
            const diff = new Date(a.requested).getTime() - new Date(b.requested).getTime();

            return direction === "asc" ? diff : -diff;
        })
            .slice(0, maxRecordsCount);

        return smsList.map((sms: Sms) => <SmsBlock key={sms.id} sms={sms}/>);
    }

    function navigateToMessageArchivePage() {
        navigate(`/dashboard/CommunicationArchivePage`, {state: communicationCollection});
    }

    if (communicationCollection.items?.length !== 0) {
        if (communicationCollection.type === CommunicationType.ArchivedDocuments
            || communicationCollection.type === CommunicationType.Emails) {
            return (
                <div className={`widget documents-widget panel ${cssStyles}`}>
                    <p className={"documents-archive-title font-weight-bold text-center"}>{header}</p>
                    <table>
                        <tbody>
                        {renderSortByDate()}
                        {renderDocumentsByDate(communicationCollection.items)}
                        </tbody>
                    </table>
                    {moreThanFiveRecords && <button
                        type="button"
                        className="btn btn-primary btn-blue-dashboard mb-3"
                        onClick={navigateToMessageArchivePage}>View more
                    </button>}
                </div>
            );
        } else if (communicationCollection.type === CommunicationType.SMS) {
            return (
                <div className={`widget documents-widget panel ${cssStyles}`}>
                    <p className={"documents-archive-title font-weight-bold text-center"}>{header}</p>
                    <table>
                        <tbody>
                        {renderSortByDate()}
                        {renderSmsByDate(communicationCollection.items)}
                        </tbody>
                    </table>
                    {moreThanFiveRecords && <button
                        type="button"
                        className="btn btn-primary btn-blue-dashboard mb-3"
                        onClick={navigateToMessageArchivePage}>View more
                    </button>}
                </div>
            );
        } else {
            return <></>
        }
    } else {
        return <></>
    }
}

export default CommunicationList;
